import { useState } from "react";
import dayjs from "dayjs";
import { DATE_FORMAT_API } from "config/settings";

type ReceiptsFilter = {
  search: string | undefined;
  companyId: ID;
  date_Gte: string | undefined;
  date_Lte: string | undefined;
};

type PaginationParams = {
  offset: number;
  limit: number;
  orderBy: string[];
};

type UseReceiptsFiltersProps = {
  companyId: ID;
  listReceipts: (options: { variables: { filter: ReceiptsFilter } & PaginationParams }) => void;
  refetch: (params: { limit: number; offset: number }) => void;
};

export default function useReceiptsFilters({
  companyId,
  listReceipts,
  refetch,
}: UseReceiptsFiltersProps) {
  const FILTER_INIT: ReceiptsFilter = {
    search: undefined,
    companyId,
    date_Gte: undefined,
    date_Lte: undefined,
  };

  const PAGINATION_INIT: PaginationParams = {
    offset: 0,
    limit: 50,
    orderBy: ["-date"],
  };

  const [filters, setFilters] = useState(FILTER_INIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const handleSearchFilters = (confirm: () => void) => {
    confirm();

    const formattedFilters = { ...filters };

    if (formattedFilters.date_Lte) {
      formattedFilters.date_Lte = dayjs(formattedFilters.date_Lte).format(DATE_FORMAT_API);
    }

    if (formattedFilters.date_Gte) {
      formattedFilters.date_Gte = dayjs(formattedFilters.date_Gte).format(DATE_FORMAT_API);
    }

    listReceipts({ variables: { filter: { ...formattedFilters }, ...PAGINATION_INIT } });
  };

  const handleResetFilter = (_filterList: unknown, confirm: () => void) => {
    confirm();
    setFilters(FILTER_INIT);
    listReceipts({ variables: { filter: { ...FILTER_INIT }, ...PAGINATION_INIT } });
  };

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    refetch({
      limit: size,
      offset: size * (page - 1),
    });
  };

  const fetchInitialData = () => {
    listReceipts({
      variables: {
        filter: filters,
        ...PAGINATION_INIT,
      },
    });
  };

  return {
    filters,
    setFilters,
    currentPage,
    pageSize,
    handleSearchFilters,
    handleResetFilter,
    handlePagination,
    fetchInitialData,
    PAGINATION_INIT,
  };
}
